import React, { Component } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Box, Checkbox, Text } from 'core/components';
import { Select } from 'core/form';

// values that map to system-level group options such as select all/none functions and (un)selected group headers
const GROUP_VALUES = {
  SELECTOR: 'group-selector',
  SELECTED: 'group-selected',
  UNSELECTED: 'group-unselected'
};

// styled box to render group controls that ships with sticky positioning
const GroupBox = styled(Box)`
  position: sticky;
  top: ${({ top }) => top || '-4px'};
  z-index: 1;
  padding: 4px 12px;
  margin: -4px -4px 8px -4px;
  background: ${({ theme }) => theme.colors.appBackground};

  & > * {
    font-size: ${({ theme, large }) => `${theme.fontSizes[large ? 'large' : 'small']}`};
  }
`;

@observer
export default class GroupedMultiSelect extends Component {
  static defaultProps = {
    selectedGroupName: 'selection', // default group header label for selected items
    unselectedGroupName: 'item', // default group header label for unselected items
    maxItems: null // optional restriction on the number of selectable items
  };

  state = {
    options: [], // operating set of options
    filter: '' // the current search string in the filter input
  };

  // returns a list of options, cleaned of special group items
  filterGroupOptions = (options) => options.filter((option) => !Object.values(GROUP_VALUES).includes(option?.value));

  // categorizes options into selected/unselected buckets
  groupOptions = (options) => {
    const { fieldValues } = this;
    // don't count the group headers and selector
    const cleanedOptions = this.filterGroupOptions(options);

    const groups = cleanedOptions.reduce(
      (groupedOptions, option) => {
        const bucket = fieldValues.includes(option.value) ? 'selected' : 'unselected';

        // add to the right bucket and preserve a complete list
        groupedOptions[bucket].push(option);
        groupedOptions.all.push(option);

        return groupedOptions;
      },
      { selected: [], unselected: [], all: [] }
    );

    groups.noneSelected = groups.selected.length === 0;
    groups.allSelected = groups.unselected.filter((o) => !o.disabled).length === 0;

    return groups;
  };

  // builds a list of renderable options, complete with a selector control and grouped headers
  buildOptions = (groupedOptions) => {
    const { selectedGroupName, unselectedGroupName } = this.props;
    const { selected, unselected, all, allSelected, noneSelected } = groupedOptions;
    // always render the selector control at the top
    let options = [{ value: GROUP_VALUES.SELECTOR }];

    if (noneSelected || allSelected) {
      // when all items are blank or selected, just show a complete, sorted list of options with no group headers
      options = options.concat(all.sort((a, b) => a.label.localeCompare(b.label)));
    } else {
      // we have a mixture of selection states, render them with applicable group headers
      options = options.concat(
        {
          label: `${selectedGroupName} (${selected.length})`,
          value: GROUP_VALUES.SELECTED
        },
        selected,
        {
          label: `${unselectedGroupName}s (${unselected.length})`,
          value: GROUP_VALUES.UNSELECTED
        },
        unselected
      );
    }

    return options;
  };

  // when the select is opened, build a list of grouped options to be rendered
  // this allows us to group the items when the select is expanded but manage them in-place
  // as selections are made, we won't automatically render them in their respective buckets in real-time to prevent "option jumping"
  handleOpen = () => {
    const groupedOptions = this.groupOptions(this.fieldOptions);
    const options = this.buildOptions(groupedOptions);
    this.setState({ options });
  };

  // captures the current value of the filter input
  // used to ensure the group selector remains rendered as an option by populating its filterLabel
  handleFilterOptionsChange = (filter) => this.setState({ filter });

  // returns overridden field state that ships with a new set of options
  // built with a group selector control at the top and disabled items based on optional max selected items setting
  get field() {
    const { fieldValues } = this;
    const { field, maxItems } = this.props;
    const { filter, options } = this.state;
    const isMaxedOut = maxItems && fieldValues.length >= maxItems;
    const nextOptions = options.map((option) => ({
      ...option,
      filterLabel: option.value === GROUP_VALUES.SELECTOR ? filter : `${option?.filterLabel}`,
      disabled: option.disabled || (isMaxedOut && fieldValues.indexOf(option.value) === -1)
    }));

    return {
      ...field,
      options: nextOptions,
      getValue: field.getValue ? field.getValue.bind(field) : () => field.value
    };
  }

  // returns the current options, cleansed of group selector and headers
  get fieldOptions() {
    const { field } = this.props;
    return this.filterGroupOptions(toJS(field.options));
  }

  get fieldValues() {
    const { field } = this.props;
    return field.getValue() || [];
  }

  valueRenderer = () => {
    const { unselectedGroupName } = this.props;
    const totalItems = this.fieldOptions.length;
    const selectedItems = this.fieldValues.length;
    const suffix = totalItems !== 1 ? 's' : '';
    const label = unselectedGroupName && `${unselectedGroupName}${suffix}`;

    return `${selectedItems} of ${totalItems} ${label}`;
  };

  /*
    Renders a toolbar at the top of the expanded options with functionality:
    1. 'All' link that adds all currently rendered and selectable items to the current list of selected options
    2. 'None' link that removes all currently rendered items from the current list of selected options

    This toolbar maintains its place at the top of expanded options by settings its 'filterLabel'
    property to the current value of the filter input. 
  */
  renderGroupSelector = (option) => {
    const { fieldValues } = this;
    const { small, large, onChange, maxItems } = this.props;
    const { value } = option;
    const { all, allSelected, noneSelected } = this.groupOptions(option.options);
    // all selectable options
    const allValues = all.filter((o) => !o.disabled).map((o) => o.value);
    // values representing a 'Select All' scenario
    // only the currently rendered options in the dropdown are added
    let allSelectedValues = [].concat(
      fieldValues,
      allValues.filter((v) => fieldValues.indexOf(v) === -1)
    );

    if (maxItems) {
      allSelectedValues = allSelectedValues.slice(0, maxItems);
    }

    // values representing a clear selection ('None') scenario
    // only the currently rendered options are removed from the currently selected items
    const clearedValues = fieldValues.filter((v) => allValues.indexOf(v) === -1);

    return (
      <GroupBox key={value} borderBottom="thin" small={small} large={large}>
        <Text fontWeight="bold" mr="4px">
          Select:
        </Text>
        <Text as="a" onClick={() => onChange(allSelectedValues)} disabled={allSelected} minimal>
          All
        </Text>
        <Text> / </Text>
        <Text as="a" onClick={() => onChange(clearedValues)} disabled={noneSelected}>
          None
        </Text>
      </GroupBox>
    );
  };

  // renders a sticky-positioned group header for the (un)selected items
  renderGroupHeader = (option) => {
    const { small, large } = this.props;
    const { label, value } = option;

    return (
      <GroupBox
        key={value}
        fontWeight="bold"
        textTransform="uppercase"
        top={large ? 25 : 22}
        borderTop={value === GROUP_VALUES.UNSELECTED && 'thin'}
        small={small}
        large={large}
      >
        <Text>{label}</Text>
      </GroupBox>
    );
  };

  optionRenderer = (option) => {
    const { small, large, optionFormatter } = this.props;
    const { selected, selectItem, disabled, label, value } = option;

    if (value === GROUP_VALUES.SELECTOR) {
      return this.renderGroupSelector(option);
    }

    if (value === GROUP_VALUES.SELECTED || value === GROUP_VALUES.UNSELECTED) {
      return this.renderGroupHeader(option);
    }

    return (
      <Text as="div" mx={1} key={value} small={small} large={large}>
        <Checkbox
          onChange={() => selectItem(value)}
          checked={selected}
          pb={1}
          display="flex"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          disabled={disabled}
        >
          {optionFormatter ? optionFormatter({ ...option, small, large }) : label}
        </Checkbox>
      </Text>
    );
  };

  render() {
    return (
      <Select
        valueRenderer={this.valueRenderer}
        {...this.props}
        optionRenderer={this.optionRenderer}
        field={this.field}
        onOpen={this.handleOpen}
        onFilterOptionsChange={this.handleFilterOptionsChange}
        keepOpen
        multi
        renderAsButton
        showFilter
        toggle
        disableFilterTools
      />
    );
  }
}
